import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/form-controls";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { MOCK_API_KEYS, DEV_KEY_SCOPES, DEV_KEY_EXPIRY_OPTIONS } from "@/data/mock";
import type { ApiKey } from "@/types";

/* DEV-01 — API Keys.
   Canonical source: prototype/pages/developer/api-keys.html (no
   current Figma frame located). One deliberate deviation: the
   canonical "Generate Key →" reveal displays a fully JWT-shaped fake
   secret (`ak_test_eyJhbGci...7f2a`) realistic enough to be mistaken
   for a live credential if copied. Per this task's explicit "never
   expose fabricated API secrets" / "make frontend-only key creation
   explicitly local/deferred" instruction, the revealed value here is
   generated client-side in an unmistakably local/mock format
   (`ak_local_mock_<random>`) with on-screen copy stating plainly that
   no backend key infrastructure exists and the value is not a usable
   credential. The native `confirm()` for Revoke is replaced with the
   app's Modal component, per the RCV-03 precedent. */

function generateMockKey(): string {
  const rand = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
  return `ak_local_mock_${rand}`;
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>(MOCK_API_KEYS);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const [keyName, setKeyName] = useState("");
  const [scopes, setScopes] = useState<Record<string, boolean>>({ "read:transfers": true, "read:wallets": true });
  const [expiry, setExpiry] = useState(DEV_KEY_EXPIRY_OPTIONS[1]);

  const activeCount = keys.filter((k) => k.status === "Active").length;
  const revokedCount = keys.filter((k) => k.status === "Revoked").length;

  function openCreate() {
    setKeyName("");
    setScopes({ "read:transfers": true, "read:wallets": true });
    setExpiry(DEV_KEY_EXPIRY_OPTIONS[1]);
    setRevealed(null);
    setCreateOpen(true);
  }

  function generateKey() {
    if (!keyName.trim()) return;
    setRevealed(generateMockKey());
  }

  function copyKey() {
    if (!revealed) return;
    navigator.clipboard.writeText(revealed).then(() => toast({ variant: "success", title: "Key copied to clipboard" }));
  }

  function doneCreating() {
    setCreateOpen(false);
    setRevealed(null);
  }

  function doRevoke() {
    if (!revokeTarget) return;
    setKeys((prev) => prev.map((k) => (k.id === revokeTarget.id ? { ...k, status: "Revoked" as const } : k)));
    toast({ variant: "success", title: `"${revokeTarget.name}" has been revoked.`, description: LOCAL_ONLY_NOTICE });
    setRevokeTarget(null);
  }

  const selectedScopes = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k);

  return (
    <div>
      <Link to="/developer" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-primary">← Back to Developers</Link>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-ink">API Keys</h1>
          <p className="text-sm text-muted">{activeCount} active keys · {revokedCount} revoked</p>
        </div>
        <Button onClick={openCreate}>+ Create API Key</Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-line bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-5 py-2.5">Name</th>
                <th className="px-3 py-2.5">Key</th>
                <th className="px-3 py-2.5">Scopes</th>
                <th className="px-3 py-2.5">Created</th>
                <th className="px-3 py-2.5">Last Used</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-5 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {keys.map((k) => (
                <tr key={k.id} className={k.status === "Revoked" ? "opacity-55" : ""}>
                  <td className="px-5 py-2.5 font-semibold text-ink">{k.name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted">{k.keyPreview}</td>
                  <td className="max-w-[200px] px-3 py-2.5 text-xs text-muted">{k.scopes}</td>
                  <td className="px-3 py-2.5 text-ink">{k.created}</td>
                  <td className="px-3 py-2.5 text-ink">{k.lastUsed}</td>
                  <td className="px-3 py-2.5">
                    <Badge variant={k.status === "Active" ? "success" : "neutral"} size="sm">{k.status}</Badge>
                  </td>
                  <td className="px-5 py-2.5">
                    {k.status === "Active" ? (
                      <Button variant="destructive" size="sm" onClick={() => setRevokeTarget(k)}>Revoke</Button>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={createOpen}
        onOpenChange={(o) => { if (!o) doneCreating(); else setCreateOpen(true); }}
        title={revealed ? "API Key Created" : "Create API Key"}
        description={revealed ? "Copy this key now. It will not be shown again." : "API keys grant programmatic access. Use the minimum scopes required."}
      >
        {!revealed ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted" htmlFor="key-name">Key Name</label>
              <input
                id="key-name"
                type="text"
                placeholder="e.g. CI/CD Pipeline Key"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
              />
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Scopes</div>
              <div className="grid grid-cols-2 gap-1.5">
                {DEV_KEY_SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-2 rounded-md border border-line px-2 py-1.5 text-xs text-ink">
                    <Checkbox checked={!!scopes[s]} onCheckedChange={(v) => setScopes((prev) => ({ ...prev, [s]: v }))} ariaLabel={s} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Expiry</div>
              <div className="flex gap-2">
                {DEV_KEY_EXPIRY_OPTIONS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setExpiry(e)}
                    className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-semibold ${expiry === e ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2.5">
              <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button disabled={!keyName.trim()} onClick={generateKey}>Generate Key →</Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-line bg-console-bg p-4">
              <span className="break-all font-mono text-xs text-success">{revealed}</span>
              <Button size="sm" onClick={copyKey}>Copy</Button>
            </div>
            <div className="mb-4 rounded-md border border-warning-border bg-warning-soft px-3 py-2 text-xs font-medium text-warning">
              ⚠ Locally generated for this session only — no backend key infrastructure exists. This is not a usable credential.
            </div>
            <div className="mb-4 text-sm text-muted">
              <strong className="text-ink">Scopes:</strong> {selectedScopes.join(", ") || "none"}<br />
              <strong className="text-ink">Expires:</strong> {expiry}
            </div>
            <div className="flex justify-end">
              <Button onClick={doneCreating}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!revokeTarget}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
        title={`Revoke "${revokeTarget?.name}"?`}
        description="This will immediately invalidate all requests using this key."
        footer={
          <>
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doRevoke}>Revoke</Button>
          </>
        }
      >
        <p className="text-sm text-muted">This action cannot be undone.</p>
      </Modal>
    </div>
  );
}
